import { Textfield } from '@digdir/designsystemet-react';
import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styled from '../../../../../../styled';
import { useSolcelleFormContext } from '../utils';

type SolarOrientationFieldName =
  | `kwpPanels.${number}.azimuth`
  | `kwpPanels.${number}.tilt`
  | `sqmPanels.${number}.azimuth`
  | `sqmPanels.${number}.tilt`;

type SolarOrientationInputProps = {
  error?: ReactNode;
  kind: 'azimuth' | 'tilt';
  label: string;
  name: SolarOrientationFieldName;
  pattern: {
    message: string;
    value: RegExp;
  };
  validate: (value?: number | string) => string | true;
  value?: number | string;
};

const parseDegree = (value: number | string | undefined, min: number, max: number) => {
  if (value === undefined || value === '') {
    return undefined;
  }

  const parsedValue = typeof value === 'number' ? value : Number(value.replace(',', '.'));

  if (!Number.isFinite(parsedValue) || parsedValue < min || parsedValue > max) {
    return undefined;
  }

  return parsedValue;
};

const formatDegree = (value: number, suffix: string) => `${Math.round(value)} ${suffix}`;

const azimuthDirectionKeys = ['north', 'northEast', 'east', 'southEast', 'south', 'southWest', 'west', 'northWest'] as const;

const getAzimuthDirectionKey = (degrees: number) => {
  const normalizedDegrees = ((degrees % 360) + 360) % 360;
  const sectorIndex = Math.floor((normalizedDegrees + 22.5) / 45) % azimuthDirectionKeys.length;

  return azimuthDirectionKeys[sectorIndex];
};

export const SolarOrientationInput = ({ error, kind, label, name, pattern, validate, value }: SolarOrientationInputProps) => {
  const { t } = useTranslation();
  const { register, setValue } = useSolcelleFormContext();
  const degreeSuffix = t('register.solar.form.suffix.grader');
  const min = 0;
  const max = kind === 'azimuth' ? 359 : 90;
  const fallbackValue = kind === 'azimuth' ? 180 : 30;
  const sliderValue = Math.round(parseDegree(value, min, max) ?? fallbackValue);
  const azimuthDirection = kind === 'azimuth' ? t(`register.solar.form.panel.azimuth.direction.${getAzimuthDirectionKey(sliderValue)}`) : undefined;

  const updateFromSlider = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(name, Number(event.currentTarget.value), {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  return (
    <OrientationField>
      <Textfield
        type="text"
        inputMode="numeric"
        label={label}
        suffix={degreeSuffix}
        error={error}
        {...register(name, {
          validate,
          pattern
        })}
      />
      <Slider
        type="range"
        min={min}
        max={max}
        step="1"
        value={sliderValue}
        onChange={updateFromSlider}
        aria-label={`${label}, ${formatDegree(sliderValue, degreeSuffix)}`}
      />
      <IllustrationFrame>
        {kind === 'azimuth' ? <CompassIllustration rotation={sliderValue} /> : <TiltIllustration tilt={sliderValue} />}
        {azimuthDirection && <DirectionLabel>{t('register.solar.form.panel.azimuth.directionLabel', { direction: azimuthDirection })}</DirectionLabel>}
      </IllustrationFrame>
    </OrientationField>
  );
};

const CompassIllustration = ({ rotation }: { rotation: number }) => (
  <Svg viewBox="0 0 120 120" role="img" aria-hidden="true">
    <path d="M 8 60 A 52 52 0 0 1 112 60 L 60 60 Z" fill="currentColor" opacity="0.04" />
    <path d="M 8 60 A 52 52 0 0 0 112 60 L 60 60 Z" fill="#ffd43b" opacity="0.22" />
    <circle cx="60" cy="60" r="52" fill="transparent" stroke="currentColor" strokeWidth="3" />
    <text x="60" y="20" textAnchor="middle">
      N
    </text>
    <text x="102" y="64" textAnchor="middle">
      Ø
    </text>
    <text x="60" y="106" textAnchor="middle">
      S
    </text>
    <text x="18" y="64" textAnchor="middle">
      V
    </text>
    <g transform={`rotate(${rotation} 60 60)`}>
      <line x1="60" y1="50" x2="60" y2="38" stroke="var(--ds-color-base-default)" strokeLinecap="round" strokeWidth="2.5" />
      <polygon points="60,32 55,40 65,40" fill="var(--ds-color-base-default)" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.75" />
      <polygon points="45,53 75,53 76,75 44,75" fill="var(--ds-color-base-default)" stroke="currentColor" strokeLinejoin="round" strokeWidth="3" />
      <line className="panel-detail" x1="55" y1="53" x2="54.5" y2="75" />
      <line className="panel-detail" x1="65" y1="53" x2="65.5" y2="75" />
      <line className="panel-detail" x1="44.6" y1="60.5" x2="75.4" y2="60.5" />
      <line className="panel-detail" x1="44.3" y1="68" x2="75.7" y2="68" />
    </g>
  </Svg>
);

const TiltIllustration = ({ tilt }: { tilt: number }) => {
  const originX = 24;
  const originY = 88;
  const panelLength = 78;
  const panelWidth = 16;
  const radians = (tilt * Math.PI) / 180;
  const panelEndX = originX + Math.cos(radians) * panelLength;
  const panelEndY = originY - Math.sin(radians) * panelLength;
  const normalX = -Math.sin(radians) * panelWidth;
  const normalY = -Math.cos(radians) * panelWidth;
  const panelTopStartX = originX + normalX;
  const panelTopStartY = originY + normalY;
  const panelTopEndX = panelEndX + normalX;
  const panelTopEndY = panelEndY + normalY;
  const arcRadius = 30;
  const arcEndX = originX + Math.cos(radians) * arcRadius;
  const arcEndY = originY - Math.sin(radians) * arcRadius;
  const panelPointAt = (fraction: number, offset = 0) => ({
    x: originX + Math.cos(radians) * panelLength * fraction + normalX * offset,
    y: originY - Math.sin(radians) * panelLength * fraction + normalY * offset
  });
  const panelThird = panelPointAt(1 / 3);
  const panelThirdTop = panelPointAt(1 / 3, 1);
  const panelTwoThirds = panelPointAt(2 / 3);
  const panelTwoThirdsTop = panelPointAt(2 / 3, 1);
  const panelMiddle = panelPointAt(0, 0.5);
  const panelMiddleEnd = panelPointAt(1, 0.5);

  return (
    <Svg viewBox="0 0 120 120" role="img" aria-hidden="true">
      <line x1={originX - 8} y1={originY} x2="112" y2={originY} stroke="currentColor" strokeDasharray="4 4" strokeWidth="2" />
      <path
        d={`M ${originX + arcRadius} ${originY} A ${arcRadius} ${arcRadius} 0 0 0 ${arcEndX} ${arcEndY}`}
        fill="none"
        stroke="var(--ds-color-base-default)"
        strokeWidth="4"
      />
      <polygon
        points={`${panelTopStartX},${panelTopStartY} ${panelTopEndX},${panelTopEndY} ${panelEndX},${panelEndY} ${originX},${originY}`}
        fill="var(--ds-color-base-default)"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="3"
      />
      <line className="panel-detail" x1={panelThird.x} y1={panelThird.y} x2={panelThirdTop.x} y2={panelThirdTop.y} />
      <line className="panel-detail" x1={panelTwoThirds.x} y1={panelTwoThirds.y} x2={panelTwoThirdsTop.x} y2={panelTwoThirdsTop.y} />
      <line className="panel-detail" x1={panelMiddle.x} y1={panelMiddle.y} x2={panelMiddleEnd.x} y2={panelMiddleEnd.y} />
      <circle cx={originX} cy={originY} r="4" fill="var(--ds-color-background-default)" stroke="currentColor" strokeWidth="3" />
    </Svg>
  );
};

const OrientationField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--ds-size-2);
`;

const Slider = styled.input`
  accent-color: var(--ds-color-base-default);
  inline-size: 100%;
  margin: 0;
  touch-action: pan-y;
`;

const Svg = styled.svg`
  color: var(--ds-color-text-default);
  block-size: 6.75rem;
  inline-size: auto;
  max-width: 8rem;

  text {
    fill: currentColor;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .panel-detail {
    stroke: var(--ds-color-background-default);
    stroke-width: 1.5;
  }
`;

const IllustrationFrame = styled.div`
  align-items: center;
  background: var(--ds-color-background-default);
  block-size: 9.75rem;
  border: 1px solid var(--ds-color-border-subtle);
  border-radius: var(--ds-border-radius-md);
  display: flex;
  flex-direction: column;
  gap: var(--ds-size-1);
  justify-content: center;
  padding: var(--ds-size-2);
`;

const DirectionLabel = styled.p`
  color: var(--ds-color-text-default);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
  text-align: center;
`;
